/**
 * The demo's plot: which roles it is over, and where its numbers come from.
 *
 * Its own module rather than a section of `main.ts`, and the reason is a test.
 * `force-graph-flow.test.ts` had to REPRODUCE the graph's role choice — "not
 * imported, `main.ts` runs a whole page on import" — so the demo's real choice
 * and the choice under test are two pieces of code that agree today. Nothing
 * here touches the DOM, so the test imports the thing that ships.
 */

import {
  labelForRole, handleOf, PLOT_AXIS_KINDS,
  type BoundValues, type CapabilityInputs, type ContinuousPlotInput,
  type IR, type PlotPoint,
} from '@morph/runtime';
import { CAPABILITIES, ROLE_KIND, type Role, type TLayoutSpec } from '@morph/spec';
import type { ObjectShape } from '@morph/generation';
import { withoutViewState } from './capability-view.js';

/**
 * Which roles a plot is over, chosen host-side.
 *
 * The same division the graph draws: role choice belongs to whoever knows what
 * the pool MEANS, and the capability sees only the closed channel names. What
 * is different here is that a plot has to choose *two* roles that make a
 * sentence together, and the wrong pair draws a real chart of nothing —
 * duration against duration is a diagonal line, and it is not obviously a bug
 * on screen.
 *
 * So the preference is explicit: a moment on x and a judgement on y, because
 * "how has this changed" is the question a collection with a date and a rating
 * is actually asking. `PLOT_AXIS_KINDS` comes from the renderer rather than
 * from a list here — a host choosing from its own copy is the drift task 0042
 * names, and it would show up as a binding that validates and is then refused.
 */
export function plotAxes(shape: ObjectShape):
  { x?: Role; y?: Role; series?: Role; label: Role } {
  const magnitudes = shape.roles
    .filter((r) => PLOT_AXIS_KINDS.includes(ROLE_KIND[r.role]))
    .map((r) => r.role);
  // **y first.** Choosing x first and taking y from what was left made the
  // answer depend on the order the shape happened to list its roles in: a pool
  // of `[score, quantity]` and no date put `score` on x, which is the opposite
  // of what the paragraph above says this function does. Found by @Codex.
  //
  // y is a judgement, so it is drawn from the magnitudes that are NOT dates —
  // a date is a position, and "how much" is not a question it answers. `score`
  // first among those, then any other magnitude. The last fallback is for a
  // pool whose only magnitudes are dates: created against modified is a real
  // chart, and refusing it to keep this rule tidy would cost more than it buys.
  const undated = magnitudes.filter((r) => ROLE_KIND[r] !== 'date');
  const y = undated.find((r) => r === 'score') ?? undated[0] ?? magnitudes[1];
  // Never the same role on both axes. y = x draws a perfect diagonal, which
  // looks like a finding.
  const rest = magnitudes.filter((r) => r !== y);
  const x = rest.find((r) => ROLE_KIND[r] === 'date') ?? rest[0];
  const series = shape.roles.find((r) => ROLE_KIND[r.role] === 'enum')?.role;
  // The label is NOT a channel — `CAPABILITY_CHANNELS['continuous-plot']` is
  // x, y and series, and adding a fourth to carry a caption would widen the
  // closed vocabulary for a host convenience. It travels as an argument to the
  // input builder instead, which is the same boundary: the renderer receives a
  // string it was handed and never a role it went looking for.
  const label = shape.roles.find((r) => r.role === 'title')?.role
    ?? shape.roles.find((r) => ROLE_KIND[r.role] === 'text')?.role
    ?? ('title' as Role);
  return {
    ...(x ? { x } : {}), ...(y ? { y } : {}), ...(series ? { series } : {}), label,
  };
}

/** The four marks the renderer draws, in the order the demo offers them. */
export const PLOT_MARKS = ['point', 'line', 'bar', 'area'] as const;
export type PlotMark = (typeof PLOT_MARKS)[number];

/** Turn an ordinary generated spec into a plot request. Same shape as
 *  `graphSpec`, and deliberately so: a second capability that needed a second
 *  mechanism to be reachable would mean decision 0010's registry was open in
 *  name only. */
export function plotSpec(
  spec: TLayoutSpec, shape: ObjectShape, mark: PlotMark | undefined = 'point',
): TLayoutSpec {
  const { x, y, series } = plotAxes(shape);
  // The scale follows the ROLE, not a constant. This declared `scaleX: 'time'`
  // unconditionally while `plotAxes` will happily bind a plain number to x when
  // a pool has no date — so a quantity axis was labelled as time, which is a
  // spec that says something the data does not. It rendered identically, since
  // `time` and `linear` are the same geometry here, and that is exactly what
  // made it worth fixing: the spec is what the taste model learns from and what
  // a future log or ordinal scale would read. Found by @Codex.
  const scaleX = x && ROLE_KIND[x] === 'date' ? 'time' : 'linear';
  return {
    // Controls dropped with the root they drove — see `withoutViewState`.
    ...withoutViewState(spec),
    target: { ...spec.target, cardinality: 'large-set' },
    root: {
      kind: 'capability',
      id: 'pool-plot',
      capability: 'continuous-plot',
      version: CAPABILITIES['continuous-plot'].version,
      config: { mark, scaleX, scaleY: 'linear' },
      channels: {
        ...(x ? { x: { slot: 'plot-x', role: x, direction: 'read' } } : {}),
        ...(y ? { y: { slot: 'plot-y', role: y, direction: 'read' } } : {}),
        ...(series ? { series: { slot: 'plot-series', role: series, direction: 'read' } } : {}),
      },
    },
  };
}

/**
 * A role value as a number, and as the text a person should hear.
 *
 * `x` is a *number* — a date axis positions epoch milliseconds — and a name
 * built from that number says "Released 1594166400000" where it means
 * "1960-03-10". The formatting is the host's knowledge, which is why
 * `PlotPoint` carries `xText`/`yText` at all, and the accessible reading of the
 * chart is entirely these strings: the marks are aria-hidden geometry.
 *
 * Returns undefined for a value that has no magnitude, which is not an error —
 * `score` covers two thirds of the films pool, and an object without one is an
 * object that cannot be placed rather than a fault.
 */
export function magnitude(v: unknown, kind: string): { n: number; text: string } | undefined {
  if (kind === 'date') {
    const text = typeof v === 'string' ? v : String(v ?? '');
    const n = Date.parse(text);
    if (!Number.isFinite(n)) return undefined;
    // `Date.parse` does not reject an impossible calendar date, it NORMALISES
    // one: `2021-02-29` becomes 1 March. The point was then placed on 1 March
    // while `xText` and the accessible name still said 29 February — one
    // observation giving two different dates, which is the exact failure this
    // whole renderer is written against, arriving through the parser rather
    // than the geometry. Found by @Codex, who checked it against the store and
    // found `set-date` admits the value too.
    //
    // A calendar date must survive the round trip. Anything that does not is
    // not a date this host can honestly place.
    const iso = /^(\d{4}-\d{2}-\d{2})/.exec(text)?.[1];
    if (iso && new Date(n).toISOString().slice(0, 10) !== iso) return undefined;
    // The ISO text rather than a locale format: it is already readable, and it
    // is the same string on every machine that reads it back.
    return { n, text };
  }
  // `Number('')` is 0, and so are `Number(null)`, `Number([])` and
  // `Number(false)`. Coercing whatever arrived would put an object with no
  // value on the axis at zero — the precise substitution this function exists
  // to refuse, reached by the one route that does not look like a coercion.
  // Caught by the test, not by reading the code.
  const n = typeof v === 'number' ? v
    : typeof v === 'string' && v.trim() !== '' ? Number(v)
    : Number.NaN;
  return Number.isFinite(n) ? { n, text: String(n) } : undefined;
}

/**
 * The plot's data, from the rows already on screen.
 *
 * No second store round-trip, unlike the graph: a plot is over the projection
 * the lens is already showing, so the numbers on the chart and the numbers in
 * the table are read from one query and cannot disagree.
 *
 * `truncated` is the load-bearing field. The projection is capped at 200, and a
 * scatter of the first 200 of 40,000 looks *exactly* like a scatter of 40,000 —
 * there is no visual tell. So a capped query is reported as truncated and the
 * renderer refuses, which is the whole reason that flag exists.
 */
export function continuousPlotInputs(
  ir: IR, rows: readonly BoundValues[], total: number | undefined, labelRole: Role,
): CapabilityInputs | undefined {
  const node = ir.nodes.find(
    (n) => n.kind === 'capability' && n.capability === 'continuous-plot');
  if (!node) return undefined;
  const xRole = node.channels?.['x']?.role as Role | undefined;
  const yRole = node.channels?.['y']?.role as Role | undefined;
  // No axes bound means the pool has no magnitudes, and the renderer says so.
  // Returning an empty map still reaches it — returning undefined would render
  // "no data supplied", which is a different and less true sentence.
  if (!xRole || !yRole) return new Map();
  const seriesRole = node.channels?.['series']?.role as Role | undefined;

  const points: PlotPoint[] = [];
  for (const row of rows) {
    const handle = handleOf(row);
    if (!handle) continue;
    // Two manifest keys can carry the same role, and an object that populates
    // both gets that role back as an ARRAY. `magnitude` returns undefined for
    // one, so the object was dropped as though it had no value at all — and
    // with other rows still present the chart drew a plausible partial picture
    // reporting `truncated: false`. Silently discarding populated observations
    // is precisely the lie the ceiling exists to prevent, arriving through the
    // shape rather than through the page size. Found by @Codex.
    //
    // Refused rather than resolved: which of two values is the observation is a
    // question about what the pool MEANS, and neither picking the first nor
    // plotting both is an answer this host can give honestly.
    // The SERIES role too, not just the axes. Two manifest keys can carry one
    // enum role, and an object populating both returns an array — which the
    // string/boolean check below turned into `undefined`, silently merging that
    // observation into the unnamed group and drawing a plausible chart with the
    // wrong grouping and the wrong legend. The same defect as the axes, one
    // channel over, and it was left there by a fix that named two roles when it
    // should have named three. Found by @Codex.
    if (Array.isArray(row[xRole]) || Array.isArray(row[yRole])
      || (seriesRole !== undefined && Array.isArray(row[seriesRole]))) {
      return new Map([[node.i, {
        capability: 'continuous-plot' as const,
        plot: {
          points: [], xLabel: labelForRole(xRole), yLabel: labelForRole(yRole),
          truncated: true,
        },
      }]]);
    }
    const x = magnitude(row[xRole], ROLE_KIND[xRole]);
    const y = magnitude(row[yRole], ROLE_KIND[yRole]);
    // An object missing either value is not plottable. Dropping it is honest;
    // substituting a zero would put it on the axis as though it scored nothing.
    if (!x || !y) continue;
    // A `bool` value is a populated enum-kind value, and `true`/`false` are
    // both real groups. Accepting only non-empty strings dropped them, so both
    // collapsed into the unnamed series, their values vanished from the
    // accessible names, and no legend was drawn for a series the spec had
    // bound. Found by @Codex.
    const raw = seriesRole === undefined ? undefined : row[seriesRole];
    const series = typeof raw === 'boolean' ? (raw ? 'Yes' : 'No')
      : typeof raw === 'string' && raw ? raw
      : undefined;
    points.push({
      handle, x: x.n, y: y.n, xText: x.text, yText: y.text,
      label: String(row[labelRole] ?? 'Object'),
      ...(series ? { series } : {}),
    });
  }

  const plot: ContinuousPlotInput = {
    points,
    xLabel: labelForRole(xRole),
    yLabel: labelForRole(yRole),
    truncated: total !== undefined && total > rows.length,
  };
  return new Map([[node.i, { capability: 'continuous-plot', plot }]]);
}
